// Same-origin API. Rule S1: the frontend NEVER reads an environment-dependent value (no
// REACT_APP_* host/URL/secret). It calls /api/... as a relative path; the browser resolves it
// against whatever host served the page — preview hits preview, prod hits prod, after cutover
// app.junocre.com hits that. One build, correct everywhere, nothing to point and nothing to flip.

// A SERVER ERROR IS ALWAYS A STRING BY THE TIME IT LEAVES HERE (founder, 12 Oct 2026).
//
// FastAPI answers a validation failure with `detail` as an ARRAY of objects —
// [{type, loc, msg, input, url}] — not a string. Every console screen does `setErr(e.detail || "…")`
// and renders `{err}`, so one 422 put an array of objects into a React child, React threw #31
// ("Objects are not valid as a React child"), and the WHOLE staff console unmounted to a blank page.
// A missing form field took the page down.
//
// The fix belongs here, in the one place every call already passes through, not at each of the
// ~20 call sites — a screen added next month cannot be relied upon to remember. Each caller keeps
// `setErr(e.detail || "…")` untouched and now gets a readable sentence.
//
// The raw payload is kept on `e.detailRaw` for debugging. It is never the thing rendered.
function _readable(detail, status) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;

  // FastAPI 422: an array of {loc, msg, type}. "body" and the index are plumbing an operator does
  // not need; the FIELD NAME and the reason are the whole of the useful information.
  if (Array.isArray(detail)) {
    const parts = detail.map((d) => {
      if (typeof d === "string") return d;
      if (!d || typeof d !== "object") return String(d);
      const loc = Array.isArray(d.loc)
        ? d.loc.filter((p) => p !== "body" && typeof p !== "number").join(".")
        : "";
      const msg = d.msg || d.type || "is not valid";
      return loc ? `${loc}: ${msg}` : msg;
    }).filter(Boolean);
    if (parts.length) return parts.join("; ");
  }

  if (typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.detail === "string") return detail.detail;
    try {
      return JSON.stringify(detail);          // last resort: still a STRING, never an object
    } catch (_) {
      /* fall through */
    }
  }
  return `HTTP ${status}`;
}

export function readableDetail(detail, status) {
  return _readable(detail, status);
}

export class ApiError extends Error {
  constructor(status, detail, raw) {
    const text = _readable(detail, status) || `HTTP ${status}`;
    super(text);
    this.status = status;
    this.detail = text;      // ALWAYS a string — safe to render as a React child
    this.detailRaw = raw !== undefined ? raw : detail;  // the untouched payload, for debugging only
  }
}

export async function apiFetch(path, { method = "GET", body, token } = {}) {
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const headers = { "X-Juno-Client": "web" };  // first-party marker (CSRF: cross-site can't set it)
  if (!isForm) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`/api${path}`, {
    method,
    headers,
    body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch (_) {
    /* non-JSON */
  }
  if (!res.ok) {
    const raw = data && (data.detail !== undefined ? data.detail : data.message);
    throw new ApiError(res.status, raw, raw);
  }
  return data;
}
